import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useApp, optimizedImage, imageSrcSet } from "../store";
import useDocumentMeta, { injectJsonLd } from "../hooks/useDocumentMeta";
import { ArrowLeft } from "lucide-react";

/** Tiny safe Markdown-ish renderer for our seeded articles.
 * Supports: # / ## / ### headings, **bold**, _italic_, [text](url), - lists,
 *   ![alt](img-url) images (block-level, rendered as figure), blank-line paragraphs.
 * Intentionally minimal — no raw HTML. */
function renderMd(md) {
  if (!md) return null;
  const blocks = md.split(/\n\n+/);
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    // Block-level image: whole block is a single ![alt](url) markdown token.
    const imgMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]+)")?\)$/);
    if (imgMatch) {
      const [, alt, src, caption] = imgMatch;
      return (
        <figure key={i} className="my-10">
          <img
            src={optimizedImage(src, 1200, 78)}
            srcSet={imageSrcSet(src, [500, 800, 1200, 1600], 78)}
            sizes="(max-width: 1024px) 100vw, 800px"
            alt={alt || ""}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover"
          />
          {(caption || alt) && (
            <figcaption className="text-xs text-[#8A8D8A] italic mt-3 text-center">{caption || alt}</figcaption>
          )}
        </figure>
      );
    }
    if (trimmed.startsWith("### ")) return <h3 key={i} className="font-serif-display text-2xl mt-12 mb-4">{inline(trimmed.slice(4))}</h3>;
    if (trimmed.startsWith("## ")) return <h2 key={i} className="font-serif-display text-3xl mt-14 mb-5">{inline(trimmed.slice(3))}</h2>;
    if (trimmed.startsWith("# ")) return <h1 key={i} className="font-serif-display text-4xl md:text-5xl mt-4 mb-8 leading-tight">{inline(trimmed.slice(2))}</h1>;
    if (trimmed.startsWith("- ")) {
      const items = trimmed.split("\n").map(l => l.replace(/^- /, ""));
      return <ul key={i} className="list-disc pl-6 my-5 space-y-2 text-[#3C3D3C]">{items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>;
    }
    return <p key={i} className="text-base text-[#3C3D3C] leading-relaxed my-5">{inline(trimmed)}</p>;
  });
}
function inline(text) {
  // links → [t](u), bold **, italic _
  const parts = [];
  const re = /(\*\*[^*]+\*\*|_[^_]+_|\[[^\]]+\]\([^)]+\))/g;
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={m.index}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("_")) parts.push(<em key={m.index}>{tok.slice(1, -1)}</em>);
    else if (tok.startsWith("[")) {
      const lm = tok.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (lm) parts.push(<Link key={m.index} to={lm[2]} className="text-copper underline hover:opacity-80">{lm[1]}</Link>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export default function BlogPost() {
  const { slug } = useParams();
  const { API } = useApp();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);

  useDocumentMeta({
    title: article ? article.title : "Article",
    description: article?.excerpt,
    image: article?.cover_image_url || undefined,
    type: "article",
  });

  useEffect(() => {
    axios.get(`${API}/articles/${slug}`).then(r => setArticle(r.data)).catch(() => setArticle({ _notfound: true }));
    axios.get(`${API}/articles`).then(r => setRelated((r.data || []).filter(a => a.slug !== slug).slice(0, 3)));
  }, [API, slug]);

  useEffect(() => {
    if (article && !article._notfound) {
      injectJsonLd("ld-article", {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        image: article.cover_image_url || undefined,
        datePublished: article.created_at,
        dateModified: article.updated_at,
        author: { "@type": "Organization", name: "House of Absolutes KA" },
        publisher: {
          "@type": "Organization",
          name: "House of Absolutes KA",
          logo: { "@type": "ImageObject", url: "https://houseofabsoluteska.com/logo512.png" },
        },
      });
    }
    return () => { document.getElementById("ld-article")?.remove(); };
  }, [article]);

  if (!article) return <div className="pt-32 px-6 md:px-12 max-w-[900px] mx-auto text-[#8A8D8A] font-serif-display text-2xl italic">Loading…</div>;
  if (article._notfound) return (
    <div className="pt-32 px-6 md:px-12 max-w-[900px] mx-auto">
      <h1 className="font-serif-display text-5xl mb-6">Article not found</h1>
      <Link to="/blog" className="overline text-copper">← Back to journal</Link>
    </div>
  );

  return (
    <div className="pt-32 pb-24 px-6 md:px-12" data-testid="blog-post-page">
      <article className="max-w-[760px] mx-auto">
        <Link to="/blog" className="overline text-xs text-[#8A8D8A] hover:text-copper inline-flex items-center gap-2 mb-10" data-testid="back-to-blog">
          <ArrowLeft size={14} /> Back to journal
        </Link>

        <div className="flex flex-wrap gap-3 mb-4">
          {(article.tags || []).map(t => (
            <span key={t} className="overline text-[10px] text-copper">{t}</span>
          ))}
        </div>

        {article.cover_image_url && (
          <div className="aspect-[16/9] bg-clay overflow-hidden mb-10">
            <img src={article.cover_image_url} alt={article.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="prose-custom">
          {renderMd(article.body_md)}
        </div>

        <div className="border-t border-[#E5E1D8] mt-20 pt-10">
          <div className="overline text-[10px] text-[#8A8D8A] mb-6">Continue reading</div>
          <div className="grid md:grid-cols-3 gap-8">
            {related.map(r => (
              <Link key={r.id} to={`/blog/${r.slug}`} className="group block" data-testid={`related-article-${r.slug}`}>
                <h3 className="font-serif-display text-xl mb-2 group-hover:text-copper transition-colors">{r.title}</h3>
                <p className="text-sm text-[#5C5E5C] line-clamp-2">{r.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}
